import { supabase } from "@/integrations/supabase/client";
import { config } from "@/config";
import { isTurnstileRequiredResponse } from "@/security/botDetection/types";
import {
  signRequest,
  isRequestSigningEnabled,
  observeServerClock,
  parseExpiredRequestError,
  applyExpiredRequestCorrection,
  getServerClockOffsetMs,
} from "@/security/requestSigning";

// Get the URL and anon key from the supabase client
export const SUPABASE_URL = `${config.SUPABASE_URL}/functions/v1`;
export const SUPABASE_ANON_KEY = config.SUPABASE_ANON_KEY;

// API Error type for requests with status codes
export interface ApiError extends Error {
  status: number;
  response?: any;
  turnstileRequired?: boolean;
}

// Type guard for API errors
export function isApiError(error: unknown): error is ApiError {
  return error instanceof Error && 'status' in error && typeof (error as any).status === 'number';
}

// Global variable to store current selected role
// This will be updated by the AuthContext
let currentSelectedRole: any = null;

// Bot detection state - will be updated by BotDetectionProvider
let botDetectionEnabled = false;
let getEncryptedBotScoreFn: (() => Promise<string>) | null = null;
let currentTurnstileToken: string | null = null;
let showTurnstileChallengeFn: (() => void) | null = null;

// Bot score cache to avoid redundant detection on concurrent requests
let cachedBotScorePromise: Promise<string> | null = null;
let cachedBotScoreTimestamp = 0;
const BOT_SCORE_CACHE_TTL_MS = 5000; // Cache bot score for 5 seconds

/**
 * Initialize bot detection for API requests
 * Called by BotDetectionProvider on mount
 */
export function initializeBotDetection(
  getEncryptedBotScore: () => Promise<string>,
  showTurnstileChallenge: () => void
) {
  botDetectionEnabled = true;
  getEncryptedBotScoreFn = getEncryptedBotScore;
  showTurnstileChallengeFn = showTurnstileChallenge;
  console.log('[apiUtils] Bot detection initialized');
}

/**
 * Update the current Turnstile token
 * Called by BotDetectionProvider when token changes
 */
export function setTurnstileToken(token: string | null) {
  currentTurnstileToken = token;
}

/**
 * Get the show Turnstile challenge function
 */
export function getShowTurnstileChallenge() {
  return showTurnstileChallengeFn;
}

/**
 * Set the current selected role for API requests
 * This should be called by the AuthContext when selectedRole changes
 */
export function setCurrentSelectedRole(role: any) {
  currentSelectedRole = role;
  console.log('[apiUtils] Updated current selected role:', role);
}

/**
 * Get the current selected role
 */
export function getCurrentSelectedRole() {
  return currentSelectedRole;
}

/**
 * Generic API request function with error handling.
 */
export async function apiRequest<T>(
  url: string,
  options?: {
    method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH' | 'OPTIONS';
    body?: any;
    headers?: Record<string, string>;
    responseType?: 'json' | 'text' | 'blob';
  }
): Promise<T> {
  console.log(`[apiUtils] Making ${options?.method || 'GET'} request to ${url}`);
  
  // Get the session to check for an auth token
  const { data: { session } } = await supabase.auth.getSession();
  const authToken = session?.access_token;
  
  const headers: HeadersInit = {
    ...(options?.headers || {})
  };

  // Only add Content-Type header for non-FormData requests
  if (!(options?.body instanceof FormData)) {
    headers['Content-Type'] = 'application/json';
  }
  
  headers['apikey'] = SUPABASE_ANON_KEY;
  
  // Add selected role information to headers if available
  if (currentSelectedRole) {
    headers['X-User-Role'] = JSON.stringify(currentSelectedRole);
    console.log('[apiUtils] Adding role to headers:', currentSelectedRole);
  }
  
  // Use the session token if available, otherwise use the anon key
  if (authToken) {
    headers['Authorization'] = `Bearer ${authToken}`;
    console.log('[apiUtils] Using auth token for request');
  } else {
    headers['Authorization'] = `Bearer ${SUPABASE_ANON_KEY}`;
    console.log('[apiUtils] Using anon key for request');
  }

  // Add bot detection headers if enabled (cached to avoid redundant detection on concurrent requests)
  if (botDetectionEnabled && getEncryptedBotScoreFn) {
    try {
      const now = Date.now();
      if (!cachedBotScorePromise || (now - cachedBotScoreTimestamp) > BOT_SCORE_CACHE_TTL_MS) {
        cachedBotScoreTimestamp = now;
        cachedBotScorePromise = getEncryptedBotScoreFn();
      }
      const encryptedBotScore = await cachedBotScorePromise;
      if (encryptedBotScore) {
        headers['X-Bot-Score'] = encryptedBotScore;
      }
    } catch (e) {
      console.warn('[apiUtils] Failed to get bot score:', e);
      cachedBotScorePromise = null;
    }

    // Add Turnstile token if available
    if (currentTurnstileToken) {
      headers['X-Turnstile-Token'] = currentTurnstileToken;
    }
  }

  // Offset in effect when the outgoing request was stamped, so a rejection can
  // be turned into an absolute correction rather than a cumulative one.
  let offsetWhenSigned = 0;

  // Add request signing headers to prevent replay attacks.
  // Re-callable so a request rejected for clock drift can be signed afresh
  // (a new timestamp also needs a new nonce, or it trips replay detection).
  const applySigningHeaders = async () => {
    if (!isRequestSigningEnabled()) return;
    try {
      offsetWhenSigned = getServerClockOffsetMs();
      const method = options?.method || 'GET';
      const body = options?.body instanceof FormData ? undefined : options?.body;
      const signedHeaders = await signRequest(method, url, body);
      if (signedHeaders) {
        headers['X-Request-Timestamp'] = signedHeaders['X-Request-Timestamp'];
        headers['X-Request-Nonce'] = signedHeaders['X-Request-Nonce'];
        headers['X-Request-Signature'] = signedHeaders['X-Request-Signature'];
      }
    } catch (e) {
      console.warn('[apiUtils] Failed to sign request:', e);
    }
  };

  await applySigningHeaders();

  const requestOptions: RequestInit = {
    method: options?.method || 'GET',
    headers,
    mode: 'cors', // Explicitly set CORS mode
  };

  // Add body data for non-GET requests
  if (options?.method !== 'GET' && options?.method !== 'OPTIONS' && options?.body) {
    if (options.body instanceof FormData) {
      // Do not stringify FormData - pass it directly
      requestOptions.body = options.body;
      console.log('[apiUtils] Sending form data:', 
        [...options.body.entries()].map(entry => 
          entry[0] === 'file' ? 
            `${entry[0]}: [File: ${(entry[1] as File).name}]` : 
            `${entry[0]}: ${entry[1]}`
        )
      );
    } else {
      requestOptions.body = JSON.stringify(options.body);
      console.log('[apiUtils] Request body:', JSON.stringify(options.body, null, 2));
    }
  }

  let hasRetriedAfterClockCorrection = false;

  try {
    console.log('[apiUtils] Sending fetch request with options:', { 
      method: requestOptions.method, 
      url, 
      headers: Object.keys(headers),
      body: requestOptions.body ? 
        (requestOptions.body instanceof FormData ? 
          'FormData Present' : 'JSON Present') 
        : 'Not present'
    });
    
    let response = await fetch(url, requestOptions);
    console.log(`[apiUtils] Received response with status: ${response.status}`);

    // Learn the server's clock from every response so request signing stays
    // valid even when the local machine's clock has drifted.
    observeServerClock(response.headers);

    // A badly wrong local clock makes the first signed request of a session
    // fail before any response has been seen. The rejection states the drift,
    // so correct by it, re-sign, and retry once.
    if (!response.ok && !hasRetriedAfterClockCorrection) {
      const expiry = parseExpiredRequestError(
        await peekErrorMessage(response.clone())
      );
      if (expiry) {
        hasRetriedAfterClockCorrection = true;
        applyExpiredRequestCorrection(expiry, offsetWhenSigned);
        await applySigningHeaders();
        requestOptions.headers = headers;
        response = await fetch(url, requestOptions);
        console.log(
          `[apiUtils] Retried after clock correction, status: ${response.status}`
        );
        observeServerClock(response.headers);
      }
    }
    
    // For OPTIONS requests or DELETE requests with no content
    if (options?.method === 'OPTIONS' || (options?.method === 'DELETE' && response.status === 204)) {
      return {} as T;
    }

    // Check if we should parse as json, text or blob based on options or content-type header
    const responseType = options?.responseType || getResponseTypeFromHeader(response.headers);
    
    // Handle different response types
    let responseData;
    try {
      if (responseType === 'text') {
        responseData = await response.text();
        console.log('[apiUtils] Parsed text response');
      } else if (responseType === 'blob') {
        responseData = await response.blob();
        console.log('[apiUtils] Created blob from response');
      } else {
        // Default to JSON
        responseData = await response.json();
        console.log('[apiUtils] Parsed JSON response');
      }
    } catch (e) {
      console.error('[apiUtils] Failed to parse response:', e);
      throw new Error('Failed to parse server response');
    }

    // Handle API error responses
    if (!response.ok) {
      console.error('[apiUtils] Request failed:', responseData);

      // Check for Turnstile required response
      if (isTurnstileRequiredResponse(responseData)) {
        console.log('[apiUtils] Turnstile challenge required');
        if (showTurnstileChallengeFn) {
          showTurnstileChallengeFn();
        }
        const error = new Error('Security challenge required') as ApiError;
        error.status = response.status;
        error.response = responseData;
        error.turnstileRequired = true;
        throw error;
      }

      const errorMessage =
        typeof responseData === 'object' && responseData !== null ?
          responseData.message || responseData.error || 'Something went wrong' :
          'Something went wrong';

      // Create an ApiError with status code and response data
      const error = new Error(errorMessage) as ApiError;
      error.status = response.status;
      error.response = responseData;

      // Log specific information for permission denied errors
      if (response.status === 403) {
        console.warn('[apiUtils] Permission denied (403) - This error will not be retried by React Query');
      } else if (response.status === 401) {
        console.warn('[apiUtils] Unauthorized (401) - This error will not be retried by React Query');
      }

      throw error;
    }

    return responseData as T;
  } catch (error) {
    console.error('[apiUtils] Request error:', error);
    throw error;
  }
}


/**
 * Read an error message from a response clone without consuming the original
 * body. Returns null when the payload isn't JSON or carries no message.
 */
async function peekErrorMessage(response: Response): Promise<string | null> {
  try {
    const data = await response.json();
    if (data && typeof data === 'object') {
      return data.error || data.message || null;
    }
  } catch {
    // Non-JSON error body; nothing to extract.
  }
  return null;
}

/**
 * Helper function to determine the response type based on Content-Type header
 */
function getResponseTypeFromHeader(headers: Headers): 'json' | 'text' | 'blob' {
  const contentType = headers.get('Content-Type') || '';
  
  if (contentType.includes('application/json')) {
    return 'json';
  }
  
  if (contentType.includes('text/csv') || 
      contentType.includes('text/plain') ||
      contentType.includes('text/html')) {
    return 'text';
  }

  if (contentType.includes('application/octet-stream') || 
      contentType.includes('image/') ||
      contentType.includes('application/pdf')) {
    return 'blob';
  }
  
  // Default to JSON if we can't determine type
  return 'json';
}
